import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createLogin, createLogout, refresh } from '@/services/apiClient'
import { clearAllExpandedStates } from '@/utils/explorerStateStorage'
import { tokenRefreshManager } from '@/utils/tokenRefresh'
import { getTokenExpiration } from '@/utils/jwt'
import { outdatedAgentsCheckManager } from '@/utils/outdatedAgentsCheck'
import { pendingInvitationsCheckManager } from '@/utils/pendingInvitationsCheck'

/**
 * Clears localStorage while preserving agent-specific settings (encryption, OpenAI, API keys),
 * database filter preferences, UI preferences, and datasource folder expansion state
 */
function clearLocalStorageExceptEncryption() {
  // Save encryption, OpenAI, API key, database filter, and UI settings before clearing
  const settingsToPreserve: Record<string, string> = {}
  const encryptionPrefix = 'mazpin_agent_encryption_'
  const openaiPrefix = 'mazpin_agent_openai_'
  const apiKeyPrefix = 'mazpin_agent_apikey_'
  const databaseFilterKey = 'database-schema-filters'
  const sidebarCollapsedKey = 'sidebar-collapsed'
  const datasourceFolderStateKey = 'datasource-folder-state'

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (key.startsWith(encryptionPrefix) || key.startsWith(openaiPrefix) || key.startsWith(apiKeyPrefix) || key === databaseFilterKey || key === sidebarCollapsedKey || key === datasourceFolderStateKey)) {
      settingsToPreserve[key] = localStorage.getItem(key) || ''
    }
  }
  
  // Clear all localStorage
  localStorage.clear()
  
  // Restore preserved settings
  for (const [key, value] of Object.entries(settingsToPreserve)) {
    if (value) {
      localStorage.setItem(key, value)
    }
  }
}

export interface User {
  id: string
  username: string
  email?: string
  name: string
  role: 'admin' | 'user'
  createdAt: string
  authProvider?: 'google' | 'local'
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setToken: (token: string) => void
  clearAuth: () => void
  loadUserFromStorage: () => void
  refreshAccessToken: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isLoading: false,

      login: async (username: string, password: string) => {
        set({ isLoading: true })
        try {
          const { data: response } = await createLogin({
            body: {
              username,
              password,
            },
            throwOnError: true,
          })
          
          if (response.token && response.user) {
            // Add authProvider for regular login
            const userWithAuth: User = {
              ...(response.user as User),
              authProvider: 'local'
            };

            // Store in both localStorage and Zustand store
            localStorage.setItem('token', response.token);
            localStorage.setItem('user', JSON.stringify(userWithAuth));
            if (response.refreshToken) {
              localStorage.setItem('refreshToken', response.refreshToken);
              console.log('[AUTH] 💾 Refresh token stored');
            } else {
              console.warn('[AUTH] ⚠️ No refresh token in login response!');
            }

            console.log('✅ [AUTH] Login successful for user:', {
              id: response.user.id,
              username: response.user.username,
              authProvider: 'local'
            })

            if (response.token) {
              const tokenExp = getTokenExpiration(response.token);
              if (tokenExp) {
                const expiresIn = Math.floor((tokenExp - Date.now()) / 1000);
                console.log(`[AUTH] 🎫 Token received, expires in ${expiresIn} seconds`);
              }
            }

            set({
              user: userWithAuth,
              token: response.token,
              refreshToken: response.refreshToken || null,
              isLoading: false
            })

            console.log('[AUTH] 📦 Store state updated:', {
              hasToken: !!response.token,
              hasRefreshToken: !!response.refreshToken,
              user: response.user.username
            })

            // Schedule token refresh if we have a valid token
            if (response.token) {
              tokenRefreshManager.scheduleRefresh(response.token, async () => {
                return useAuthStore.getState().refreshAccessToken();
              });
            }

            // Fetch user settings after successful login
            try {
              const { useSettingsStore } = await import('./settingsStore')
              await useSettingsStore.getState().fetchSettings()
              console.log('✅ [AUTH] User settings fetched after login')
            } catch (settingsError) {
              console.error('Failed to fetch settings after login:', settingsError)
              // Don't throw - login was successful, settings fetch is optional
            }

            // Start outdated agents monitoring after login
            try {
              const { useOutdatedAgentsStore } = await import('./outdatedAgentsStore')
              useOutdatedAgentsStore.getState().startMonitoring()
              console.log('✅ [AUTH] Outdated agents monitoring started after login')
            } catch (monitoringError) {
              console.error('Failed to start outdated agents monitoring:', monitoringError)
              // Don't throw - login was successful, monitoring is optional
            }

            // Start pending invitations monitoring after login
            try {
              const { usePendingInvitationsStore } = await import('./pendingInvitationsStore')
              usePendingInvitationsStore.getState().startMonitoring()
              console.log('✅ [AUTH] Pending invitations monitoring started after login')
            } catch (monitoringError) {
              console.error('Failed to start pending invitations monitoring:', monitoringError)
              // Don't throw - login was successful, monitoring is optional
            }
            return
          }
        } catch (error) {
          set({ isLoading: false })
          throw error
        }
        // Invalid response - reset loading state and throw error
        set({ isLoading: false })
        throw new Error('Invalid login response - missing token or user data')
      },

      logout: () => {
        const currentUser = get().user
        console.log('🚪 [AUTH] Logging out user:', currentUser ? {
          id: currentUser.id,
          username: currentUser.username
        } : 'No user')

        // Clear token refresh timer
        tokenRefreshManager.clearRefresh()

        // Stop outdated agents monitoring
        outdatedAgentsCheckManager.stop()

        // Stop pending invitations monitoring
        pendingInvitationsCheckManager.stop()

        void (async () => {
          try {
            await createLogout({ throwOnError: true })
          } catch (error) {
            // Even if logout API fails, clear local state
            console.error('Logout API error:', error)
          }
        })()

        // Clear ALL state aggressively - complete reset like page refresh

        // STEP 1: Immediately clear auth state to prevent any new requests
        set({ user: null, token: null, refreshToken: null, isLoading: false })
        console.log('✅ [AUTH] Auth state cleared immediately')
        
        // STEP 2: Clear localStorage but preserve agent-specific settings
        clearLocalStorageExceptEncryption()
        // Also clear OAuth-specific storage
        sessionStorage.removeItem('oauth_redirect')
        console.log('✅ [AUTH] LocalStorage cleared (except agent settings) on logout')

        // STEP 3: Clear React Query cache to remove ALL cached data
        void import('@/services/queryClient').then(({ queryClient }) => {
          queryClient.clear()
          // Also cancel all ongoing queries
          void queryClient.cancelQueries()
          console.log('✅ [AUTH] React Query cache cleared and queries cancelled on logout')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear React Query cache on logout:', error)
        })

        // 2. Reset collections store to initial state
        // Use the new invalidateAllCollectionCaches action which clears the
        // Zustand-persisted caches (collectionDetailsCache & folderDetailsCache)
        import('./collectionsStore').then(({ useCollectionsStore }) => {
          const collectionsStore = useCollectionsStore.getState()
          // Clear all collection and folder caches
          collectionsStore.invalidateAllCollectionCaches()
          useCollectionsStore.setState({
            collections: [],
            currentCollection: null,
            isLoading: false,
            error: null,
            loadingStates: {},
            updateQueryLocks: {}
          })
          console.log('✅ [AUTH] Collections store reset on logout')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear collections store on logout:', error)
        })

        // 3. Reset query library selection store
        import('./queryLibrarySelectionStore').then(({ useQueryLibrarySelectionStore }) => {
          useQueryLibrarySelectionStore.getState().clearSelection()
          console.log('✅ [AUTH] Query library selections cleared on logout')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear query library selections on logout:', error)
        })

        // 4. Reset database users selection store
        import('./databaseUsersSelectionStore').then(({ useDatabaseUsersSelectionStore }) => {
          useDatabaseUsersSelectionStore.getState().clearAllSelections()
          console.log('✅ [AUTH] Database users selections cleared on logout')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear database users selections on logout:', error)
        })

        // 5. Reset connection store completely (including persisted lastActiveConnectionId)
        import('./connectionStore').then(({ useConnectionStore }) => {
          useConnectionStore.setState({
            connections: [],
            activeConnection: null,
            lastActiveConnectionId: null,
            isConnecting: false
          })
          console.log('✅ [AUTH] Connection store reset on logout')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear connection store on logout:', error)
        })

        // 6. Reset settings store to defaults
        import('./settingsStore').then(({ useSettingsStore }) => {
          useSettingsStore.getState().resetSettings()
          console.log('✅ [AUTH] Settings store reset on logout')
          return undefined
        }).catch((error) => {
          console.error('Failed to reset settings store on logout:', error)
        })
        
        // 7. Clear explorer expanded states
        clearAllExpandedStates()
        console.log('✅ [AUTH] Explorer states cleared on logout')

        console.log('✅ [AUTH] Complete logout state reset finished')
      },

      setUser: (user: User) => {
        set({ user })
        localStorage.setItem('user', JSON.stringify(user));
      },

      setToken: (token: string) => {
        set({ token })
        localStorage.setItem('token', token);

        // Schedule token refresh for the new token
        tokenRefreshManager.scheduleRefresh(token, async () => {
          return useAuthStore.getState().refreshAccessToken();
        });
      },

      clearAuth: () => {
        console.log('🔄 [AUTH] Clearing auth state (e.g., due to 401 error)')

        // Clear token refresh timer
        tokenRefreshManager.clearRefresh()

        // Stop outdated agents monitoring
        outdatedAgentsCheckManager.stop()

        // Stop pending invitations monitoring
        pendingInvitationsCheckManager.stop()

        // Use the same aggressive clearing as logout
        // Clear ALL state completely - complete reset like page refresh

        // STEP 1: Immediately clear auth state to prevent any new requests
        set({ user: null, token: null, refreshToken: null, isLoading: false })
        console.log('✅ [AUTH] Auth state cleared immediately')
        
        // STEP 2: Clear localStorage but preserve agent-specific settings
        clearLocalStorageExceptEncryption()
        // Also clear OAuth-specific storage
        sessionStorage.removeItem('oauth_redirect')
        console.log('✅ [AUTH] LocalStorage cleared (except agent settings) on clearAuth')
        
        // STEP 3: Clear React Query cache to remove ALL cached data
        void import('@/services/queryClient').then(({ queryClient }) => {
          queryClient.clear()
          // Also cancel all ongoing queries
          void queryClient.cancelQueries()
          console.log('✅ [AUTH] React Query cache cleared and queries cancelled on clearAuth')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear React Query cache on clearAuth:', error)
        })

        // 2. Reset collections store to initial state
        // Use the new invalidateAllCollectionCaches action which clears the
        // Zustand-persisted caches (collectionDetailsCache & folderDetailsCache)
        import('./collectionsStore').then(({ useCollectionsStore }) => {
          const collectionsStore = useCollectionsStore.getState()
          // Clear all collection and folder caches
          collectionsStore.invalidateAllCollectionCaches()
          useCollectionsStore.setState({
            collections: [],
            currentCollection: null,
            isLoading: false,
            error: null,
            loadingStates: {},
            updateQueryLocks: {}
          })
          console.log('✅ [AUTH] Collections store reset on clearAuth')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear collections store on clearAuth:', error)
        })

        // 3. Reset query library selection store
        import('./queryLibrarySelectionStore').then(({ useQueryLibrarySelectionStore }) => {
          useQueryLibrarySelectionStore.getState().clearSelection()
          console.log('✅ [AUTH] Query library selections cleared on clearAuth')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear query library selections on clearAuth:', error)
        })

        // 4. Reset database users selection store
        import('./databaseUsersSelectionStore').then(({ useDatabaseUsersSelectionStore }) => {
          useDatabaseUsersSelectionStore.getState().clearAllSelections()
          console.log('✅ [AUTH] Database users selections cleared on clearAuth')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear database users selections on clearAuth:', error)
        })

        // 5. Reset connection store completely
        import('./connectionStore').then(({ useConnectionStore }) => {
          useConnectionStore.setState({
            connections: [],
            activeConnection: null,
            lastActiveConnectionId: null,
            isConnecting: false
          })
          console.log('✅ [AUTH] Connection store reset on clearAuth')
          return undefined
        }).catch((error) => {
          console.error('Failed to clear connection store on clearAuth:', error)
        })

        // 6. Reset settings store to defaults
        import('./settingsStore').then(({ useSettingsStore }) => {
          useSettingsStore.getState().resetSettings()
          console.log('✅ [AUTH] Settings store reset on clearAuth')
          return undefined
        }).catch((error) => {
          console.error('Failed to reset settings store on clearAuth:', error)
        })
        
        // 7. Clear explorer expanded states
        clearAllExpandedStates()
        console.log('✅ [AUTH] Explorer states cleared on clearAuth')
        
        console.log('✅ [AUTH] Complete clearAuth state reset finished')
      },

      refreshAccessToken: async () => {
        const state = get();
        const { refreshToken } = state;

        if (!refreshToken) {
          console.error('[AUTH] No refresh token available, cannot refresh');
          return;
        }

        try {
          const { data: response } = await refresh({
            body: { refreshToken },
            throwOnError: true,
          });

          if (response.token) {
            // Update tokens silently - only update token and refreshToken, NOT user
            // This prevents unnecessary re-renders of components subscribed to user state
            localStorage.setItem('token', response.token);
            if (response.refreshToken) {
              localStorage.setItem('refreshToken', response.refreshToken);
            }

            // Only update tokens, not user - user data doesn't change during refresh
            set({
              token: response.token,
              refreshToken: response.refreshToken || refreshToken,
            });

            // Schedule next refresh - use a function that will get fresh state
            tokenRefreshManager.scheduleRefresh(response.token, async () => {
              return useAuthStore.getState().refreshAccessToken();
            });
          } else {
            console.error('[AUTH] ❌ Invalid refresh response - missing token');
            get().clearAuth();
          }
        } catch (error) {
          console.error('[AUTH] ❌ Token refresh failed:', error);
          // On refresh failure, clear auth and redirect to login
          get().clearAuth();
        }
      },

      loadUserFromStorage: () => {
        // Since we're using persist middleware, the state is already loaded
        // This function ensures the refresh scheduler is set up for persisted sessions
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        const refreshToken = localStorage.getItem('refreshToken');

        if (token && userStr) {
          try {
            const parsed = JSON.parse(userStr) as unknown;
            const user = (parsed && typeof parsed === 'object') ? ({
              id: typeof (parsed as { id?: unknown }).id === 'string' ? (parsed as { id?: string }).id! : '',
              username: typeof (parsed as { username?: unknown }).username === 'string' ? (parsed as { username?: string }).username! : '',
              email: typeof (parsed as { email?: unknown }).email === 'string' ? (parsed as { email?: string }).email! : undefined,
              name: typeof (parsed as { name?: unknown }).name === 'string' ? (parsed as { name?: string }).name! : '',
              role: ((): User['role'] => {
                const r = (parsed as Record<string, unknown>).role
                return r === 'admin' ? 'admin' : 'user'
              })(),
              createdAt: typeof (parsed as { createdAt?: unknown }).createdAt === 'string' ? (parsed as { createdAt?: string }).createdAt! : new Date().toISOString(),
              authProvider: ((): User['authProvider'] | undefined => {
                const p = (parsed as { authProvider?: unknown }).authProvider
                return p === 'google' ? 'google' : p === 'local' ? 'local' : undefined
              })(),
            } as User) : null;
            const currentState = get();

            // Update state if needed (persist middleware may have already loaded it)
            if (!currentState.token || !currentState.user) {
              set({ token, user, refreshToken: refreshToken || null });
            }

            // ALWAYS schedule token refresh for any valid token, even if state was already loaded
            // This is critical because persist middleware loads state but doesn't set up the scheduler
            tokenRefreshManager.scheduleRefresh(token, async () => {
              return useAuthStore.getState().refreshAccessToken();
            });

            // Start outdated agents monitoring for persisted sessions
            import('./outdatedAgentsStore').then(({ useOutdatedAgentsStore }) => {
              useOutdatedAgentsStore.getState().startMonitoring();
              console.log('✅ [AUTH] Outdated agents monitoring started for persisted session');
              return undefined
            }).catch((error) => {
              console.error('Failed to start outdated agents monitoring for persisted session:', error);
            });

            // Start pending invitations monitoring for persisted sessions
            import('./pendingInvitationsStore').then(({ usePendingInvitationsStore }) => {
              usePendingInvitationsStore.getState().startMonitoring();
              console.log('✅ [AUTH] Pending invitations monitoring started for persisted session');
              return undefined
            }).catch((error) => {
              console.error('Failed to start pending invitations monitoring for persisted session:', error);
            });
          } catch (error) {
            console.error('Error parsing stored user data:', error);
            // Clear invalid data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('refreshToken');
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
      }),
    }
  )
)
